/**
 * Phase 45 — Audio cues nhỏ tự sinh bằng Web Audio API (không cần file mp3).
 * Synthesize tones on-the-fly để giữ bundle gọn + không phụ thuộc asset.
 */

let _ctx = null;
function ctx() {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  try {
    const Cls = window.AudioContext || window.webkitAudioContext;
    _ctx = Cls ? new Cls() : null;
  } catch {
    _ctx = null;
  }
  return _ctx;
}

const PREF_KEY = "pickle_audio_enabled";

export function isAudioEnabled() {
  if (typeof localStorage === "undefined") return true;
  const v = localStorage.getItem(PREF_KEY);
  return v == null ? true : v === "1";
}

export function setAudioEnabled(enabled) {
  try { localStorage.setItem(PREF_KEY, enabled ? "1" : "0"); } catch (e) { /* noop */ }
}

function tone({ freq = 880, duration = 0.18, type = "sine", gain = 0.08, attack = 0.005 } = {}) {
  if (!isAudioEnabled()) return;
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") c.resume?.().catch(() => null);
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.value = 0;
  osc.connect(g).connect(c.destination);
  const now = c.currentTime;
  g.gain.linearRampToValueAtTime(gain, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.start(now);
  osc.stop(now + duration + 0.05);
}

/** Ding nhẹ — toast notification trọng tài */
export function dingNotification() {
  tone({ freq: 988, duration: 0.18, type: "sine", gain: 0.07 });
  setTimeout(() => tone({ freq: 1318, duration: 0.22, type: "sine", gain: 0.06 }), 110);
}

// ─────────────────────────────────────────────────────────────────
// Phase 81n — Score interaction beeps + Crowd cheer
// User spec (2026-05-07): 7a + 8c
//   - score +1: ding cao (1320 Hz)
//   - score -1: beep thấp (440 Hz)
//   - main action (start/end/forfeit): ding chuẩn (đã có dingNotification)
// ─────────────────────────────────────────────────────────────────

/** Tăng điểm — ding cao, ngắn (tactile feedback). */
export function scorePlusBeep() {
  tone({ freq: 1320, duration: 0.08, type: "sine", gain: 0.075 });
  setTimeout(() => tone({ freq: 1760, duration: 0.06, type: "sine", gain: 0.045 }), 35);
}

/** Giảm điểm — beep thấp, ngắn (báo hiệu undo). */
export function scoreMinusBeep() {
  tone({ freq: 440, duration: 0.1, type: "triangle", gain: 0.075 });
  setTimeout(() => tone({ freq: 330, duration: 0.08, type: "sine", gain: 0.05 }), 40);
}

/**
 * Phase 81n — Tiếng reo hò khán giả tự sinh bằng Web Audio API.
 * Mô phỏng: white noise burst + filter band sweep + 4 voice oscillators
 * pitch-up "wooo" + 50ms claps. Tổng ~2.4s.
 *
 * User chọn 1a: tự sinh, không dùng MP3 → bundle gọn, không phụ thuộc CDN.
 */
export function crowdCheer({ duration = 2.4, intensity = 1.0 } = {}) {
  if (!isAudioEnabled()) return;
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") c.resume?.().catch(() => null);

  const now = c.currentTime;
  const finalGain = 0.18 * intensity;

  // 1) Noise burst — đám đông ồn ã (filtered white noise, swelling envelope).
  try {
    const bufferSize = Math.floor(c.sampleRate * duration);
    const noiseBuffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.6;
    }
    const noise = c.createBufferSource();
    noise.buffer = noiseBuffer;
    const noiseFilter = c.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.value = 1100;
    noiseFilter.Q.value = 0.6;
    const noiseGain = c.createGain();
    noiseGain.gain.setValueAtTime(0.001, now);
    noiseGain.gain.exponentialRampToValueAtTime(finalGain, now + 0.18);
    noiseGain.gain.exponentialRampToValueAtTime(finalGain * 0.85, now + 1.2);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    noise.connect(noiseFilter).connect(noiseGain).connect(c.destination);
    noise.start(now);
    noise.stop(now + duration);

    // Sweep filter freq for "rising" excitement.
    noiseFilter.frequency.linearRampToValueAtTime(1700, now + 0.7);
    noiseFilter.frequency.linearRampToValueAtTime(1300, now + duration);
  } catch {
    /* noop */
  }

  // 2) "Wooo!" voice harmonics — 5 sine oscillators ghép thành chord rising.
  try {
    const chord = [
      { f: 220, slide: 280, dur: duration, delay: 0, gain: 0.12 },
      { f: 330, slide: 420, dur: duration - 0.1, delay: 0.05, gain: 0.10 },
      { f: 440, slide: 550, dur: duration - 0.2, delay: 0.10, gain: 0.09 },
      { f: 550, slide: 660, dur: duration - 0.3, delay: 0.15, gain: 0.07 },
      { f: 660, slide: 880, dur: duration - 0.4, delay: 0.22, gain: 0.05 },
    ];
    chord.forEach(({ f, slide, dur, delay, gain }) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, now + delay);
      osc.frequency.linearRampToValueAtTime(slide, now + delay + 0.45);
      g.gain.setValueAtTime(0.001, now + delay);
      g.gain.exponentialRampToValueAtTime(gain * intensity, now + delay + 0.18);
      g.gain.exponentialRampToValueAtTime(gain * 0.5 * intensity, now + delay + 0.9);
      g.gain.exponentialRampToValueAtTime(0.001, now + delay + dur);
      osc.connect(g).connect(c.destination);
      osc.start(now + delay);
      osc.stop(now + delay + dur + 0.05);
    });
  } catch {
    /* noop */
  }

  // 3) Claps — 12 ngắn xen kẽ ngẫu nhiên (mô phỏng vỗ tay).
  try {
    for (let i = 0; i < 14; i++) {
      const clapDelay = 0.1 + Math.random() * (duration - 0.4);
      const clapDur = 0.05;
      const buf = c.createBuffer(1, Math.floor(c.sampleRate * clapDur), c.sampleRate);
      const arr = buf.getChannelData(0);
      for (let j = 0; j < arr.length; j++) {
        arr[j] = (Math.random() * 2 - 1) * Math.exp(-j / 200);
      }
      const src = c.createBufferSource();
      src.buffer = buf;
      const flt = c.createBiquadFilter();
      flt.type = "highpass";
      flt.frequency.value = 1500;
      const cg = c.createGain();
      cg.gain.value = 0.18 * intensity * (0.5 + Math.random() * 0.5);
      src.connect(flt).connect(cg).connect(c.destination);
      src.start(now + clapDelay);
      src.stop(now + clapDelay + clapDur);
    }
  } catch {
    /* noop */
  }
}

/** Tiếng còi khởi động — countdown Step 2 "Bắt đầu" */
export function whistleStart() {
  tone({ freq: 1760, duration: 0.45, type: "square", gain: 0.05, attack: 0.02 });
}

/** Bíp đếm ngược — 3-2-1 */
export function countdownBeep() {
  tone({ freq: 660, duration: 0.12, type: "triangle", gain: 0.07 });
}

/** Fanfare ngắn — set quyết định / chung kết */
export function fanfare() {
  if (!isAudioEnabled()) return;
  const seq = [
    [523, 0],     // C5
    [659, 120],   // E5
    [784, 240],   // G5
    [1047, 360],  // C6
    [1319, 540],  // E6
  ];
  for (const [f, t] of seq) {
    setTimeout(() => tone({ freq: f, duration: 0.22, type: "triangle", gain: 0.06 }), t);
  }
}

/** Vibrate (mobile only) */
export function vibrate(ms = 80) {
  if (typeof navigator === "undefined") return;
  if (navigator.vibrate) {
    try { navigator.vibrate(ms); } catch (e) { /* noop */ }
  }
}
